// ETJump's overbounce detector (etj_overbounce_shared.cpp,
// etj_overbounce_detector.cpp): predicts whether falling (or jumping) from the
// current height onto a floor gives an overbounce at 125/333 fps snapping:
// the player reaches the floor height with less than 0.25 units left in the
// last frame, so the ground trace misses it and the clip bounces the speed.

import { f } from '../f32';
import type { Vec3 } from '../math';
import { JUMP_VELOCITY, SURF_OVERBOUNCE, type PlayerState, ENTITYNUM_NONE } from '../physics/pmove';
import { MASK_PLAYERSOLID } from '../surfaceflags';
import type { TraceResult } from '../cm/types';

/** Overbounce::stickyOffset */
export const STICKY_OFFSET = 0.25;
/** MAX_MAP_SIZE * 2 */
const MAX_TRACE_DIST = 131072;

/**
 * Overbounce::isOverbounce: does a fall that starts at startHeight with zVel
 * end in a frame whose bottom is within 0.25 units above endHeight? (float
 * arithmetic like the C++.)
 */
export function isOverbounce(zVel: number, startHeight: number, endHeight: number, zVelSnapped: number, pmoveSec: number, gravity: number): boolean {
  const a = f((f(-pmoveSec) * zVelSnapped) / 2);
  const b = f(pmoveSec * f(f(zVel - f(f(gravity * pmoveSec) / 2)) + f(zVelSnapped / 2)));
  const c = f(startHeight - endHeight);
  // no quadratic equation
  if (a === 0) return false;
  const discriminant = f(f(b * b) - f(f(4 * a) * c));
  // no real roots
  if (discriminant < 0) return false;
  const root1 = f(f(-b - f(Math.sqrt(discriminant))) / f(2 * a));
  const timeSteps = Math.floor(root1);
  const reachedHeight = f(startHeight + f(f(pmoveSec * timeSteps) * f(f(zVel - f(f(gravity * pmoveSec) / 2)) - f(f((timeSteps - 1) * zVelSnapped) / 2))));
  return timeSteps !== 0 && reachedHeight < endHeight + STICKY_OFFSET && reachedHeight > endHeight;
}

/** Overbounce::surfaceAllowsOverbounce: SURF_OVERBOUNCE forbids it (allows it on "nooverbounce" maps). */
export function surfaceAllowsOverbounce(surfaceFlags: number, noOverbounce: boolean): boolean {
  const flagged = (surfaceFlags & SURF_OVERBOUNCE) !== 0;
  return noOverbounce ? flagged : !flagged;
}

export interface OverbounceResult {
  /** Falling onto the floor below the player. */
  below: boolean;
  /** Falling onto the floor under the crosshair. */
  fall: boolean;
  /** Jumping from here onto the floor under the crosshair. */
  jump: boolean;
  /** Only with the sticky offset (etj_drawOB 2). */
  sticky: boolean;
}

export type PointTrace = (start: Vec3, end: Vec3, mask: number) => TraceResult;

/** A floor: a trace that hit an axial up/down facing plane (plane.type == PLANE_Z). */
const hitFloor = (t: TraceResult) => t.fraction !== 1 && t.plane.normal[0] === 0 && t.plane.normal[1] === 0 && Math.abs(t.plane.normal[2]) === 1;

/**
 * OverbounceDetector::beforeRender for a player state.
 * @param eye the view origin, forward the view direction
 * @param msec physics frame length
 * @param sticky also look for overbounces with the 0.25 sticky offset
 */
export function detectOverbounce(ps: PlayerState, eye: Vec3, forward: Vec3, msec: number, noOverbounce: boolean, trace: PointTrace, sticky = false): OverbounceResult {
  const out: OverbounceResult = { below: false, fall: false, jump: false, sticky: false };
  const pmoveSec = f(msec / 1000);
  const gravity = ps.gravity;
  const zVel = ps.velocity[2];
  let startHeight = f(ps.origin[2] + ps.mins[2]);
  // trap_SnapVector of the gravity of one frame
  const zVelSnapped = Math.round(f(gravity * pmoveSec));
  const ob = (z: number, start: number, end: number) => isOverbounce(z, start, end, zVelSnapped, pmoveSec, gravity);

  if (ps.groundEntityNum === ENTITYNUM_NONE) {
    const start: Vec3 = [ps.origin[0], ps.origin[1], startHeight];
    const t = trace(start, [start[0], start[1], start[2] - MAX_TRACE_DIST], MASK_PLAYERSOLID);
    if (hitFloor(t) && ob(zVel, startHeight, t.endpos[2]) && surfaceAllowsOverbounce(t.surfaceFlags, noOverbounce)) out.below = true;
  }

  const end: Vec3 = [eye[0] + forward[0] * MAX_TRACE_DIST, eye[1] + forward[1] * MAX_TRACE_DIST, eye[2] + forward[2] * MAX_TRACE_DIST];
  const t = trace(eye, end, MASK_PLAYERSOLID);
  if (!hitFloor(t)) return out;
  const endHeight = t.endpos[2];
  const allowed = surfaceAllowsOverbounce(t.surfaceFlags, noOverbounce);
  const onGround = ps.groundEntityNum !== ENTITYNUM_NONE;
  if (allowed && ob(zVel, startHeight, endHeight)) out.fall = true;
  if (allowed && onGround && ob(zVel + JUMP_VELOCITY, startHeight, endHeight)) out.jump = true;
  if (sticky && !out.jump && !out.fall && startHeight !== endHeight && allowed) {
    startHeight = f(startHeight + STICKY_OFFSET);
    if (ob(zVel, startHeight, endHeight)) out.fall = out.sticky = true;
    if (onGround && ob(zVel + JUMP_VELOCITY, startHeight, endHeight)) out.jump = out.sticky = true;
  }
  return out;
}
